import React, { useCallback, useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../UI/Button';
import { ManagerList } from './ManagerList';
import { AddManagerModal } from './AddManagerModal';
import { EditManagerModal } from './EditManagerModal';
import { eventTeamApi, type TeamResponse } from '../../api/eventTeam';
import type { EventManager } from '../../types';
import styles from './TeamManagementPanel.module.css';

interface TeamManagementPanelProps {
  eventId: string;
  canManageTeam: boolean;
}

export function TeamManagementPanel({ eventId, canManageTeam }: TeamManagementPanelProps) {
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState<EventManager | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventTeamApi.list(eventId);
      setTeam(data);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      const msg = e.response?.data?.error ?? 'Failed to load team';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className={styles['panel']}>
      <div className={styles['header']}>
        <div>
          <h2 className={styles['title']}>Team</h2>
          <p className={styles['subtitle']}>
            Invite collaborators and pick exactly what they can do for this event.
          </p>
        </div>
        {canManageTeam && (
          <Button
            variant="primary"
            onClick={() => setAddOpen(true)}
            disabled={loading}
          >
            <UserPlus className={styles['btn-icon']} />
            <span>Add manager</span>
          </Button>
        )}
      </div>

      {error && !loading && (
        <div className={styles['error']}>{error}</div>
      )}

      {loading || !team ? (
        <div className={styles['loading']}>Loading team…</div>
      ) : (
        <ManagerList
          creator={team.creator}
          managers={team.managers}
          canEdit={canManageTeam}
          onEdit={setEditing}
          emptyHint={
            canManageTeam
              ? 'Add someone by email and choose which actions they can take.'
              : 'The event creator hasn’t added any other managers yet.'
          }
        />
      )}

      <AddManagerModal
        eventId={eventId}
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onAdded={load}
      />
      <EditManagerModal
        eventId={eventId}
        manager={editing}
        isOpen={editing !== null}
        onClose={() => setEditing(null)}
        onChanged={load}
      />
    </div>
  );
}
